import { GP_VIEW_PERMISSION } from '@supply-mover-2-0/shared';
import {
	DEFAULT_ORDER_STATUS_COLORS,
	OrderStatus,
	OrderStatusColors,
	OrderStatusLabels,
} from '../../../utils/orderStatus';
import type { ListBuilderColumn } from '../../types';

/**
 * Shared column definitions for order lists
 */

/**
 * Status and Origin column with status badge, alert icon, and origin badge
 * Uses centralized colors from orderStatus.ts for consistency across the application.
 */
export const createStatusOriginColumn = (): ListBuilderColumn => ({
	id: 'status-origin',
	fields: [
		{
			id: 'status-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'status',
					objectTable: 'order',
				},
			},
			styleConfig: {
				size: 'xs',
				colorRule: {
					type: 'conditional',
					rules: [
						// Alert status error takes priority
						{
							field: { fieldName: 'alertStatus' },
							operator: 'equals',
							value: '3',
							color: { bg: '#dc2626', text: '#ffffff' },
						},
						// Status-based colors from centralized config
						{
							field: { fieldName: 'status' },
							operator: 'equals',
							value: OrderStatus.UNSUBMITTED,
							color: OrderStatusColors[OrderStatus.UNSUBMITTED],
						},
						{
							field: { fieldName: 'status' },
							operator: 'equals',
							value: OrderStatus.PENDING,
							color: OrderStatusColors[OrderStatus.PENDING],
						},
						{
							field: { fieldName: 'status' },
							operator: 'equals',
							value: OrderStatus.SENT_TO_ERP,
							color: OrderStatusColors[OrderStatus.SENT_TO_ERP],
						},
						{
							field: { fieldName: 'status' },
							operator: 'equals',
							value: OrderStatus.APPROVED,
							color: OrderStatusColors[OrderStatus.APPROVED],
						},
						{
							field: { fieldName: 'status' },
							operator: 'equals',
							value: OrderStatus.SHIPPED,
							color: OrderStatusColors[OrderStatus.SHIPPED],
						},
						{
							field: { fieldName: 'status' },
							operator: 'equals',
							value: OrderStatus.HELD,
							color: OrderStatusColors[OrderStatus.HELD],
						},
						{
							field: { fieldName: 'status' },
							operator: 'equals',
							value: OrderStatus.CANCELLED,
							color: OrderStatusColors[OrderStatus.CANCELLED],
						},
						{
							field: { fieldName: 'status' },
							operator: 'equals',
							value: OrderStatus.APPROVAL_REQUIRED,
							color: OrderStatusColors[OrderStatus.APPROVAL_REQUIRED],
						},
						{
							field: { fieldName: 'status' },
							operator: 'equals',
							value: OrderStatus.OVERRIDE_REJECTED,
							color: OrderStatusColors[OrderStatus.OVERRIDE_REJECTED],
						},
						{
							field: { fieldName: 'status' },
							operator: 'equals',
							value: OrderStatus.CANCELLATION_REQUESTED,
							color: OrderStatusColors[OrderStatus.CANCELLATION_REQUESTED],
						},
						{
							field: { fieldName: 'status' },
							operator: 'equals',
							value: OrderStatus.SUBMITTED,
							color: OrderStatusColors[OrderStatus.SUBMITTED],
						},
						{
							field: { fieldName: 'status' },
							operator: 'equals',
							value: OrderStatus.PARTIALLY_SHIPPED,
							color: OrderStatusColors[OrderStatus.PARTIALLY_SHIPPED],
						},
					],
					defaultColor: DEFAULT_ORDER_STATUS_COLORS,
				},
				valueMapping: [
					{
						value: OrderStatus.UNSUBMITTED,
						displayText: OrderStatusLabels[OrderStatus.UNSUBMITTED],
					},
					{
						value: OrderStatus.PENDING,
						displayText: OrderStatusLabels[OrderStatus.PENDING],
					},
					{
						value: OrderStatus.SENT_TO_ERP,
						displayText: OrderStatusLabels[OrderStatus.SENT_TO_ERP],
					},
					{
						value: OrderStatus.APPROVED,
						displayText: OrderStatusLabels[OrderStatus.APPROVED],
					},
					{
						value: OrderStatus.SHIPPED,
						displayText: OrderStatusLabels[OrderStatus.SHIPPED],
					},
					{
						value: OrderStatus.HELD,
						displayText: OrderStatusLabels[OrderStatus.HELD],
					},
					{
						value: OrderStatus.CANCELLED,
						displayText: OrderStatusLabels[OrderStatus.CANCELLED],
					},
					{
						value: OrderStatus.APPROVAL_REQUIRED,
						displayText: OrderStatusLabels[OrderStatus.APPROVAL_REQUIRED],
					},
					{
						value: OrderStatus.OVERRIDE_REJECTED,
						displayText: OrderStatusLabels[OrderStatus.OVERRIDE_REJECTED],
					},
					{
						value: OrderStatus.CANCELLATION_REQUESTED,
						displayText: OrderStatusLabels[OrderStatus.CANCELLATION_REQUESTED],
					},
					{
						value: OrderStatus.SUBMITTED,
						displayText: OrderStatusLabels[OrderStatus.SUBMITTED],
					},
					{
						value: OrderStatus.PARTIALLY_SHIPPED,
						displayText: OrderStatusLabels[OrderStatus.PARTIALLY_SHIPPED],
					},
				],
			},
			displayStyle: 'badge',
		},
		{
			id: 'alert-status-icon',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'alertStatus',
					objectTable: 'order',
				},
			},
			displayStyle: 'icon',
			styleConfig: {
				size: 'lg',
				iconName: 'exclamation-triangle',
				colorRule: {
					type: 'static',
					staticColor: { bg: 'transparent', text: '#dc2626' },
				},
				tooltip:
					'This order has an error. Click the order to view full error details.',
			},
			conditionalVisibility: {
				field: 'alertStatus',
				condition: 'equals',
				value: '3',
			},
		},
		{
			id: 'line-break-1',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'origin-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'origin', objectTable: 'order' },
			},
			styleConfig: {
				size: 'xs',
				colorRule: {
					type: 'value-mapping',
					valueMapping: {
						'1': { bg: '#e5e7eb', text: '#374151' },
						'2': { bg: '#ddd6fe', text: '#6b21a8' },
						'3': { bg: '#dbeafe', text: '#1e40af' },
					},
				},
				valueMapping: [
					{
						color: { bg: '#e5e7eb', text: '#374151' },
						value: '1',
						displayText: 'CRM',
					},
					{
						color: { bg: '#ddd6fe', text: '#6b21a8' },
						value: '2',
						displayText: 'ERP',
					},
					{
						color: { bg: '#dbeafe', text: '#1e40af' },
						value: '3',
						displayText: 'E-Commerce',
					},
				],
			},
			displayStyle: 'badge',
		},
	],
	headers: [
		{ id: 'h1-status', text: 'Status' },
		{ id: 'h1-break', text: '', displayStyle: 'line_break' },
		{ id: 'h1-origin', text: 'Origin' },
	],
	minWidth: 120,
	sortable: true,
	alignment: 'left',
	sortFields: [{ direction: 'ASC', fieldName: 'status', objectTable: 'order' }],
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'top',
});

/**
 * Order and ERP numbers column
 */
export const createOrderErpNumbersColumn = (): ListBuilderColumn => ({
	id: 'order-erp-numbers',
	fields: [
		{
			id: 'external-id-field',
			link: {
				type: 'object',
				enabled: true,
				idField: '_id',
				objectTable: 'order',
			},
			dataConfig: {
				type: 'conditional',
				conditions: [
					{
						field: 'externalId2',
						operator: 'not_equals',
						value: null,
						dataConfig: {
							type: 'field',
							field: { fieldName: 'externalId2', objectTable: 'order' },
						},
					},
				],
				defaultDataConfig: {
					type: 'field',
					field: { fieldName: 'externalId', objectTable: 'order' },
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'static', staticColor: { text: '#3b82f6' } },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-ids',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'internal-id-field',
			link: {
				type: 'object',
				enabled: true,
				idField: '_id',
				objectTable: 'order',
			},
			dataConfig: {
				type: 'field',
				field: { fieldName: '_id', objectTable: 'order' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'static', staticColor: { text: '#3b82f6' } },
			},
			displayStyle: 'text',
		},
	],
	headers: [
		{ id: 'h2-external', text: 'External ID' },
		{ id: 'h2-break', text: '', displayStyle: 'line_break' },
		{ id: 'h2-internal', text: 'Internal ID' },
	],
	minWidth: 100,
	sortable: true,
	alignment: 'left',
	sortFields: [{ direction: 'ASC', fieldName: 'id', objectTable: 'order' }],
	widthConfig: { mode: 'flexible', preset: 'small' },
	verticalAlignment: 'top',
});

/**
 * Client info column with name and address
 */
export const createClientInfoColumn = (): ListBuilderColumn => ({
	id: 'client-info',
	fields: [
		{
			id: 'client-name-field',
			link: {
				type: 'object',
				enabled: true,
				idField: '_fk_client__id',
				objectTable: 'client',
			},
			dataConfig: {
				type: 'field',
				field: { fieldName: 'clientName', objectTable: 'order' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'static', staticColor: { text: '#3b82f6' } },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-3',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'client-address-field',
			dataConfig: {
				type: 'template',
				template: '{clientAddress1} {clientAddress2}',
				templateFields: [],
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-4',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'client-city-state-field',
			dataConfig: {
				type: 'template',
				template: '{clientCity}, {clientState} {clientZip}',
				templateFields: [],
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h3', text: 'Client' }],
	minWidth: 180,
	sortable: true,
	alignment: 'left',
	sortFields: [
		{ direction: 'ASC', fieldName: 'clientName', objectTable: 'order' },
	],
	widthConfig: { mode: 'flexible', preset: 'large' },
	verticalAlignment: 'top',
});

/**
 * Dates column with order date and last updated
 */
export const createDatesColumn = (): ListBuilderColumn => ({
	id: 'dates',
	fields: [
		{
			id: 'date-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'date', objectTable: 'order' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
				formatOptions: { dateFormat: 'datetime-short' },
				allowTextWrap: false,
			},
			displayStyle: 'date',
		},
		{
			id: 'line-break-5',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'updated-date-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: '_modified_date', objectTable: 'order' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
				formatOptions: { dateFormat: 'datetime-short' },
				allowTextWrap: false,
			},
			displayStyle: 'date',
		},
	],
	headers: [
		{ id: 'h4-date', text: 'Date' },
		{ id: 'h4-break', text: '', displayStyle: 'line_break' },
		{ id: 'h4-updated', text: 'Last Updated' },
	],
	minWidth: 110,
	sortable: true,
	alignment: 'left',
	sortFields: [{ direction: 'DESC', fieldName: 'date', objectTable: 'order' }],
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'top',
});

/**
 * Reps column with assigned rep and submitted by
 */
export const createRepsColumn = (): ListBuilderColumn => ({
	id: 'reps',
	fields: [
		{
			id: 'assigned-rep-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'assignedRepName', objectTable: 'order' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
		{
			id: 'line-break-6',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'submitted-by-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'submitterName', objectTable: 'order' },
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
	],
	headers: [
		{ id: 'h5-assigned', text: 'Assigned Rep' },
		{ id: 'h5-break', text: '', displayStyle: 'line_break' },
		{ id: 'h5-submitted', text: 'Submitted By' },
	],
	minWidth: 140,
	sortable: true,
	alignment: 'left',
	sortFields: [
		{ direction: 'ASC', fieldName: 'assignedRepName', objectTable: 'order' },
	],
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'top',
});

/**
 * Order lines column
 */
export const createOrderLinesColumn = (): ListBuilderColumn => ({
	id: 'order-lines',
	fields: [
		{
			id: 'lines-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'numberOfLines', objectTable: 'order' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h6', text: 'Lines' }],
	minWidth: 60,
	sortable: true,
	alignment: 'left',
	sortFields: [
		{ direction: 'DESC', fieldName: 'numberOfLines', objectTable: 'order' },
	],
	widthConfig: { mode: 'flexible', preset: 'small' },
	verticalAlignment: 'top',
});

/**
 * Order costs column with tax, freight, and discount
 */
export const createOrderCostsColumn = (): ListBuilderColumn => ({
	id: 'order-costs',
	fields: [
		{
			id: 'tax-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'tax', objectTable: 'order' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'currency',
		},
		{
			id: 'line-break-7',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'freight-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'freight', objectTable: 'order' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'currency',
		},
		{
			id: 'line-break-8',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'discount-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'discount', objectTable: 'order' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'currency',
		},
	],
	headers: [
		{ id: 'h7-tax', text: 'Tax' },
		{ id: 'h7-break1', text: '', displayStyle: 'line_break' },
		{ id: 'h7-freight', text: 'Freight' },
		{ id: 'h7-break2', text: '', displayStyle: 'line_break' },
		{ id: 'h7-discount', text: 'Discount' },
	],
	minWidth: 120,
	sortable: true,
	alignment: 'right',
	sortFields: [{ direction: 'DESC', fieldName: 'tax', objectTable: 'order' }],
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'top',
});

/**
 * Order totals column with subtotal and total
 */
export const createOrderTotalsColumn = (): ListBuilderColumn => ({
	id: 'order-totals',
	fields: [
		{
			id: 'subtotal-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'subtotal', objectTable: 'order' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'currency',
		},
		{
			id: 'line-break-9',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'total-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'total', objectTable: 'order' },
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'currency',
		},
	],
	headers: [
		{ id: 'h8-subtotal', text: 'Subtotal' },
		{ id: 'h8-break', text: '', displayStyle: 'line_break' },
		{ id: 'h8-total', text: 'Total' },
	],
	minWidth: 120,
	sortable: true,
	alignment: 'right',
	sortFields: [{ direction: 'DESC', fieldName: 'total', objectTable: 'order' }],
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'top',
});

/**
 * GP totals column
 */
export const createGpTotalsColumn = (): ListBuilderColumn => ({
	id: 'gp-totals',
	fields: [
		{
			id: 'total-gp-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'totalGP', objectTable: 'order' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'currency',
			// Field-level permission keeps the column out of CSV exports.
			// In-table rendering uses GP-name detection in FieldRenderer to
			// show a lock icon, so the header remains visible while cells
			// indicate the lock state explicitly.
			requiredPermission: GP_VIEW_PERMISSION,
		},
	],
	headers: [{ id: 'h-total-gp', text: 'GP' }],
	minWidth: 120,
	sortable: true,
	alignment: 'right',
	sortFields: [
		{ direction: 'DESC', fieldName: 'totalGP', objectTable: 'order' },
	],
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'top',
});

/**
 * Standard column set for all order lists
 */
export const createStandardOrderColumns = (): Array<ListBuilderColumn> => [
	createStatusOriginColumn(),
	createOrderErpNumbersColumn(),
	createClientInfoColumn(),
	createDatesColumn(),
	createRepsColumn(),
	createOrderLinesColumn(),
	createOrderCostsColumn(),
	createOrderTotalsColumn(),
	createGpTotalsColumn(),
];
